import React, { useMemo } from 'react';
import SalesDetailPage from './SalesDetailPage';
import {
  GIVING_BY_FUND_COLUMNS,
  SORT_TYPES,
  getDefaultVisibleColumns,
  getDefaultHiddenColumns,
} from '../constants/givingByFundColumnsConfig';
import { compactCurrency } from '../utils/reportingUtils';

const EMPTY_FUNDS = [];

const buildFilterGroups = (funds) => {
  if (!funds || funds.length === 0) return [];

  return [
    {
      label: 'Fund',
      filters: [
        {
          id: 'fund_id',
          label: 'Fund',
          type: 'checkbox',
          options: funds.map((f) => ({ value: String(f.id), label: f.name })),
        },
      ],
    },
  ];
};

const buildDetailApiFilters = (activeFilters) => {
  const params = {};
  if (activeFilters.fund_id && activeFilters.fund_id.length > 0) params.fund_id = activeFilters.fund_id;
  return params;
};

// The fund name links to the full-page donations drill-down, forwarding the
// active date window so the listed gifts reconcile to this row's Total Raised.
const drilldownHref = (teamId, row, dateRange) => {
  const params = new URLSearchParams({ drilldown_id: row.id });
  if (dateRange && dateRange.start) params.set('start_date', dateRange.start);
  if (dateRange && dateRange.end) params.set('end_date', dateRange.end);
  return `/teams/${teamId}/reporting/donations/giving-by-fund/gifts?${params.toString()}`;
};

const CONFIG = {
  pageTitle: 'Giving by Fund',
  pageSubtitle: 'Total raised per fund with donor and campaign counts. Shows lifetime totals by default — pick a date range to window the results.',
  sidebarKey: 'donations_giving_by_fund',
  defaultPreset: 'all_time',
  // Single option => the "View by" toggle is hidden; giving has no event-date mode.
  dateModeOptions: [{ key: 'order_date', label: 'Order Date' }],

  columns: GIVING_BY_FUND_COLUMNS,
  sortTypes: SORT_TYPES,
  getDefaultVisibleColumns,
  getDefaultHiddenColumns,
  localStorageKey: (teamId) => `reportingGivingByFundColumns_${teamId}`,
  firstColumnId: 'name',
  defaultSortRules: [{ field: 'total_raised', direction: 'desc' }],

  kpiFromDetailResponse: true,
  detailEndpoint: (teamId) => `/teams/${teamId}/reporting/donations/giving-by-fund/data.json`,
  detailResponseKey: 'funds',
  exportBasePath: (teamId) => `/teams/${teamId}/reporting/donations/giving-by-fund/export`,
  detailErrorMessage: 'Failed to load fund data.',
  detailTableTitle: 'Funds',
  emptyMessage: 'No funds found.',
  searchPlaceholder: 'Search funds...',
  paginationLabel: 'funds',
  itemLabel: (count) => `fund${count !== 1 ? 's' : ''}`,

  buildDetailApiFilters,
  filterTitle: 'Filter Funds',
  filterSubtitle: 'Apply filters to narrow funds.',

  buildKpiCards: (current, prior) => {
    const p = prior || {};
    return [
      { label: 'Total Raised', value: compactCurrency(current.total_raised || 0), current: current.total_raised || 0, prior: p.total_raised || 0 },
      { label: 'Funds', value: (current.fund_count || 0).toLocaleString(), current: current.fund_count || 0, prior: p.fund_count || 0 },
      { label: 'Unique Donors', value: (current.donor_count || 0).toLocaleString(), current: current.donor_count || 0, prior: p.donor_count || 0 },
      { label: 'Total Gifts', value: (current.gift_count || 0).toLocaleString(), current: current.gift_count || 0, prior: p.gift_count || 0 },
    ];
  },
  kpiGridColumns: 4,

  renderCellValue: (col, row, { currencyFormatter: cf, dateRange, team }) => {
    switch (col.id) {
      case 'name': return (
        <a href={drilldownHref(team.id, row, dateRange)} style={{ color: 'var(--sl-fg-brand, #1982c4)', fontWeight: 600 }}>
          {row.name || '—'}
        </a>
      );
      case 'total_raised': return cf.format(row.total_raised || 0);
      case 'donor_count': return (row.donor_count || 0).toLocaleString();
      case 'gift_count': return (row.gift_count || 0).toLocaleString();
      case 'campaign_count': return (row.campaign_count || 0).toLocaleString();
      default: return '—';
    }
  },
};

function ReportingGivingByFundPage(props) {
  const funds = props.funds || EMPTY_FUNDS;
  const config = useMemo(
    () => ({ ...CONFIG, buildFilterGroups: () => buildFilterGroups(funds) }),
    [funds]
  );
  return <SalesDetailPage {...props} config={config} />;
}

export default ReportingGivingByFundPage;
